import { useCallback } from 'react';
import { useSelector } from '~/_shared/utils/hooks/useSelector';
import { createStateSelector } from '~/_shared/utils/memoize/createSelector';
import type {
  BoundaryFilterBoundariesRequest, BoundaryFilterRequest,
} from '~/spreadsheet/filter/boundary/spreadsheetFilterBoundary.types';
import type { BoundaryTerritoryFilterGroups } from '~/spreadsheet/filter/boundaryTerritory/spreadsheetFilterBoundaryTerritory.types';
import { isArtificialBoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.helpers';
import { type RowsPerSpreadsheetWithBasicData } from '../../../../../map/map/lassoTool/rowsPerSpreadsheet';
import { useGetMarkersInArea } from '../../../../../map/map/lassoTool/useGetMarkersInArea';
import {
  type BoundaryId,
  type BoundaryTerritoryGroupId,
  type BoundaryTerritoryId,
} from '../../../../../store/boundaries/boundaryIdentifier.type';
import {
  createRequestFromBoundaryTerritoryGroupSettings,
  getBoundaryTerritoryGroupMatchupRequest,
} from '../../../../../store/boundaryTerritoryGroups/boundaryTerritoryGroups.factory';
import {
  findBoundaryTerritoryGroupSelector, useGetBoundaryTerritoryGroup,
} from '../../../../../store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';

export type MarkersFetchDataResults = {
  rowsData: ReadonlyArray<RowsPerSpreadsheetWithBasicData>;
  useMainFilters: boolean;
};

type AreaFilters = {
  boundaries?: BoundaryFilterBoundariesRequest;
  boundaryTerritories?: BoundaryTerritoryFilterGroups[];
};

export const useMarkersInBoundary = () => {
  const { getBoundaryTerritoryGroupById } = useGetBoundaryTerritoryGroup();
  const { getMarkersInArea } = useGetMarkersInArea();
  const getBoundaryTerritoriesFilter = useGetBoundaryTerritoryFilterGroups();
  const getBoundariesFilter = useGetBoundaryFilterRequest();

  const getMarkersInBoundaryArea = useCallback((
    boundaryTerritoryGroupId: BoundaryTerritoryGroupId, areaFilters: AreaFilters
  ): Promise<MarkersFetchDataResults> => {
    const boundaryTerritoryGroup = getBoundaryTerritoryGroupById(boundaryTerritoryGroupId);

    if (!boundaryTerritoryGroup) {
      return Promise.reject();
    }

    const useMainFilters = !boundaryTerritoryGroup.settings.ignoreFilters;

    return getMarkersInArea({
      useMainFilters,
      ...areaFilters,
    })
      .then(rowsData => ({
        useMainFilters,
        rowsData,
      }));
  }, [getBoundaryTerritoryGroupById, getMarkersInArea]);

  const getMarkersInBoundary = useCallback((
    boundaryTerritoryGroupId: BoundaryTerritoryGroupId, boundaryId: BoundaryId
  ): Promise<MarkersFetchDataResults> => {
    return getMarkersInBoundaryArea(boundaryTerritoryGroupId, {
      boundaries: getBoundariesFilter(boundaryTerritoryGroupId, [boundaryId])?.boundaries,
    });
  }, [getBoundariesFilter, getMarkersInBoundaryArea]);

  const getMarkersInBoundaryTerritory = useCallback((
    boundaryTerritoryGroupId: BoundaryTerritoryGroupId, boundaryTerritoryId: BoundaryTerritoryId
  ): Promise<MarkersFetchDataResults> => {
    return getMarkersInBoundaryArea(boundaryTerritoryGroupId, {
      boundaryTerritories: getBoundaryTerritoriesFilter(boundaryTerritoryGroupId, boundaryTerritoryId),
    });
  }, [getBoundaryTerritoriesFilter, getMarkersInBoundaryArea]);

  return {
    getMarkersInBoundary,
    getMarkersInBoundaryTerritory,
  };
};

export const getBoundaryTerritoryFilterGroupsSelector = createStateSelector([
  findBoundaryTerritoryGroupSelector,
], (getBoundaryTerritoryGroupById) => (
  boundaryTerritoryGroupId: BoundaryTerritoryGroupId,
  boundaryTerritoryId: BoundaryTerritoryId,
): BoundaryTerritoryFilterGroups[] | undefined => {
  const boundaryTerritoryGroup = getBoundaryTerritoryGroupById(boundaryTerritoryGroupId);

  if (!boundaryTerritoryGroup) {
    return undefined;
  }

  return [{
    type: 'or',
    boundary_territories: [boundaryTerritoryId],
    definition: {
      ...(isArtificialBoundaryTerritoryGroup(boundaryTerritoryGroup)) ? {} : { boundary_territory_group_id: boundaryTerritoryGroup.boundaryTerritoryGroupId },
      boundary_group_id: boundaryTerritoryGroup.boundaryGroupId,
      matchings: getBoundaryTerritoryGroupMatchupRequest(boundaryTerritoryGroup.matchings),
      settings: createRequestFromBoundaryTerritoryGroupSettings(boundaryTerritoryGroup.settings),
    },
  }];
});

export const useGetBoundaryTerritoryFilterGroups = () => useSelector(getBoundaryTerritoryFilterGroupsSelector);

export const getBoundaryFilterRequestSelector = createStateSelector([
  findBoundaryTerritoryGroupSelector,
], (getBoundaryTerritoryGroupById) => (
  boundaryTerritoryGroupId: BoundaryTerritoryGroupId,
  boundaryIds: BoundaryId[],
): BoundaryFilterRequest | undefined => {
  const boundaryTerritoryGroup = getBoundaryTerritoryGroupById(boundaryTerritoryGroupId);

  if (!boundaryTerritoryGroup) {
    return undefined;
  }

  return {
    type: 'or',
    boundaries: {
      [boundaryTerritoryGroup.boundaryGroupId]: {
        boundary_ids: boundaryIds,
        matchings: getBoundaryTerritoryGroupMatchupRequest(boundaryTerritoryGroup.matchings),
      },
    },
  };
});

export const useGetBoundaryFilterRequest = () => useSelector(getBoundaryFilterRequestSelector);
